import React, { useState } from 'react';
import { Card } from './ui/Card';
import { ProcessorGoal, LoanFile } from '../types/goals';
import { Badge } from './ui/Badge';
import { FileCheck, AlertCircle, Clock, Zap, Eye, Target } from 'lucide-react';
import { Button } from './ui/Button';
import { FileListModal } from './FileListModal';
interface ProcessorGoalCardProps {
  goal: ProcessorGoal | null;
  isManagerView?: boolean;
}
// Mock file data
const MOCK_AWC_FILES: LoanFile[] = [{
  id: '1',
  borrowerName: 'John & Sarah Martinez',
  loanAmount: 425000,
  propertyAddress: '123 Oak Street, San Diego, CA',
  daysInStage: 3,
  loanOfficer: 'Michael Chen',
  status: 'Waiting on income verification',
  priority: 'high',
  openConditions: 5
}, {
  id: '2',
  borrowerName: 'Robert Thompson',
  loanAmount: 380000,
  propertyAddress: '456 Maple Ave, Austin, TX',
  daysInStage: 5,
  loanOfficer: 'Sarah Jenkins',
  status: 'Pending title review',
  priority: 'medium',
  openConditions: 3
}, {
  id: '3',
  borrowerName: 'Emily & David Wilson',
  loanAmount: 520000,
  propertyAddress: '789 Pine Road, Seattle, WA',
  daysInStage: 2,
  loanOfficer: 'Amanda Ross',
  status: 'Appraisal ordered',
  priority: 'low',
  openConditions: 2
}, {
  id: '4',
  borrowerName: 'Lisa Anderson',
  loanAmount: 295000,
  propertyAddress: '321 Elm Street, Portland, OR',
  daysInStage: 7,
  loanOfficer: 'James Miller',
  status: 'Waiting on HOA documents',
  priority: 'high',
  openConditions: 8
}, {
  id: '5',
  borrowerName: 'Michael & Jennifer Brown',
  loanAmount: 475000,
  propertyAddress: '654 Cedar Lane, Denver, CO',
  daysInStage: 4,
  loanOfficer: 'Sarah Jenkins',
  status: 'Insurance verification needed',
  priority: 'medium',
  openConditions: 4
}, {
  id: '6',
  borrowerName: 'Christopher Lee',
  loanAmount: 340000,
  propertyAddress: '987 Birch Court, Phoenix, AZ',
  daysInStage: 1,
  loanOfficer: 'Michael Chen',
  status: 'Initial review complete',
  priority: 'low',
  openConditions: 2
}, {
  id: '7',
  borrowerName: 'Patricia & James Garcia',
  loanAmount: 410000,
  propertyAddress: '147 Willow Drive, Nashville, TN',
  daysInStage: 6,
  loanOfficer: 'Amanda Ross',
  status: 'Credit supplement requested',
  priority: 'high',
  openConditions: 6
}, {
  id: '8',
  borrowerName: 'Daniel Rodriguez',
  loanAmount: 365000,
  propertyAddress: '258 Spruce Street, Charlotte, NC',
  daysInStage: 3,
  loanOfficer: 'James Miller',
  status: 'Employment verification pending',
  priority: 'medium',
  openConditions: 3
}];
const MOCK_APPRAISAL_FILES: LoanFile[] = [{
  id: '9',
  borrowerName: 'Thomas & Maria Johnson',
  loanAmount: 445000,
  propertyAddress: '369 Redwood Ave, Sacramento, CA',
  daysInStage: 2,
  loanOfficer: 'Michael Chen',
  status: 'Appraisal review in progress',
  priority: 'medium',
  openConditions: 2
}, {
  id: '10',
  borrowerName: 'Susan White',
  loanAmount: 315000,
  propertyAddress: '741 Cypress Road, Tampa, FL',
  daysInStage: 4,
  loanOfficer: 'Sarah Jenkins',
  status: 'Value concerns - reviewing comps',
  priority: 'high',
  openConditions: 3
}, {
  id: '11',
  borrowerName: 'Kevin & Rachel Miller',
  loanAmount: 490000,
  propertyAddress: '852 Magnolia Lane, Atlanta, GA',
  daysInStage: 1,
  loanOfficer: 'Amanda Ross',
  status: 'Appraisal cleared',
  priority: 'low',
  openConditions: 1
}, {
  id: '12',
  borrowerName: 'Brian Taylor',
  loanAmount: 385000,
  propertyAddress: '963 Hickory Street, Raleigh, NC',
  daysInStage: 3,
  loanOfficer: 'James Miller',
  status: 'Waiting on final conditions',
  priority: 'medium',
  openConditions: 2
}];
export function ProcessorGoalCard({
  goal,
  isManagerView = false
}: ProcessorGoalCardProps) {
  const [isAwcModalOpen, setIsAwcModalOpen] = useState(false);
  const [isAppraisalModalOpen, setIsAppraisalModalOpen] = useState(false);
  if (!goal) {
    return <Card className="max-w-6xl mx-auto text-center py-12" data-id="element-203">
        <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-204">
          <AlertCircle className="w-8 h-8 text-slate-400" data-id="element-205" />
        </div>
        <h3 className="text-lg font-medium text-slate-900 mb-2" data-id="element-206">
          No Pipeline Data
        </h3>
        <p className="text-slate-500 max-w-sm mx-auto mb-6" data-id="element-207">
          {isManagerView ? "This processor doesn't have pipeline data for this month yet." : 'No pipeline data available for this month.'}
        </p>
      </Card>;
  }
  const pipelineStages = [{
    stage: 'underwriting',
    count: goal.pipeline.underwriting,
    label: 'Underwriting',
    description: 'Coming up next',
    priority: 'upcoming',
    icon: Clock,
    colorClass: 'bg-slate-50 border-slate-200 text-slate-700',
    iconColorClass: 'text-slate-500',
    badgeVariant: 'neutral' as const
  }, {
    stage: 'awc',
    count: goal.pipeline.awc,
    label: 'AWC',
    description: 'Active work - needs attention',
    priority: 'active',
    icon: Zap,
    colorClass: 'bg-blue-50 border-blue-200 text-blue-900',
    iconColorClass: 'text-blue-600',
    badgeVariant: 'info' as const,
    isActive: true
  }, {
    stage: 'appraisalBack',
    count: goal.pipeline.appraisalBack,
    label: 'Appraisal Back',
    description: 'Monitor closely',
    priority: 'urgent',
    icon: Eye,
    colorClass: 'bg-amber-50 border-amber-200 text-amber-900',
    iconColorClass: 'text-amber-600',
    badgeVariant: 'warning' as const
  }, {
    stage: 'ctc',
    count: goal.ctcActual,
    label: 'CTC',
    description: `Goal: ${goal.ctcGoal}`,
    priority: 'goal',
    icon: Target,
    colorClass: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    iconColorClass: 'text-emerald-600',
    badgeVariant: 'success' as const
  }];
  return <>
      <div className="max-w-6xl mx-auto space-y-6" data-id="element-208">
        {/* Header with CTC Goal Context */}
        <div className="flex items-center justify-between bg-white rounded-lg border border-slate-200 px-6 py-4" data-id="element-209">
          <div data-id="element-210">
            <h2 className="text-lg font-semibold text-slate-900" data-id="element-211">
              Your Pipeline
            </h2>
            <p className="text-sm text-slate-500 mt-0.5" data-id="element-212">
              Focus on what needs your attention
            </p>
          </div>
          <div className="flex items-center gap-3" data-id="element-213">
            <div className="text-right" data-id="element-214">
              <div className="text-xs text-slate-500 uppercase tracking-wider font-medium" data-id="element-215">
                Monthly Goal
              </div>
              <div className="text-2xl font-bold text-slate-900" data-id="element-216">
                {goal.ctcActual}
                <span className="text-slate-400 text-lg font-medium" data-id="element-217">
                  {' '}
                  / {goal.ctcGoal}
                </span>
              </div>
            </div>
            <Badge variant={goal.ctcActual >= goal.ctcGoal ? 'success' : goal.ctcActual >= goal.ctcGoal * 0.8 ? 'warning' : 'neutral'} data-id="element-218">
              {Math.round(goal.ctcActual / goal.ctcGoal * 100)}% Complete
            </Badge>
          </div>
        </div>

        {/* Pipeline Stage Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-219">
          {pipelineStages.map(stage => {
          const Icon = stage.icon;
          const isActive = stage.stage === 'awc';
          return <div key={stage.stage} className={`relative rounded-xl border-2 p-6 transition-all hover:shadow-md ${stage.colorClass} ${isActive ? 'md:col-span-2 lg:col-span-1 ring-2 ring-blue-600 ring-offset-2' : ''}`} data-id="element-220">
                {isActive && <div className="absolute -top-2 left-4" data-id="element-221">
                    <Badge variant="info" className="shadow-sm" data-id="element-222">
                      <Zap className="w-3 h-3 mr-1" data-id="element-223" />
                      ACTIVE
                    </Badge>
                  </div>}

                <div className="flex items-start justify-between mb-4" data-id="element-224">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${isActive ? 'bg-blue-100' : 'bg-white'}`} data-id="element-225">
                    <Icon className={`w-6 h-6 ${stage.iconColorClass}`} data-id="element-226" />
                  </div>
                  <Badge variant={stage.badgeVariant} data-id="element-227">
                    {stage.count} files
                  </Badge>
                </div>

                <h3 className="text-lg font-semibold mb-1" data-id="element-228">{stage.label}</h3>
                <p className={`text-sm ${isActive ? 'text-blue-700' : 'opacity-75'}`} data-id="element-229">
                  {stage.description}
                </p>

                {isActive && stage.count > 0 && <Button variant="primary" size="sm" className="w-full mt-4" onClick={() => setIsAwcModalOpen(true)} data-id="element-230">
                    View Files
                  </Button>}

                {stage.stage === 'appraisalBack' && stage.count > 0 && <Button variant="outline" size="sm" className="w-full mt-4" onClick={() => setIsAppraisalModalOpen(true)} data-id="element-231">
                    Review
                  </Button>}
              </div>;
        })}
        </div>

        {/* Quick Stats Summary */}
        <div className="bg-white rounded-lg border border-slate-200 p-6" data-id="element-232">
          <div className="flex items-center justify-between" data-id="element-233">
            <div data-id="element-234">
              <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-2" data-id="element-235">
                Pipeline Summary
              </h3>
              <p className="text-slate-600" data-id="element-236">
                <span className="font-semibold text-slate-900" data-id="element-237">
                  {goal.pipeline.underwriting + goal.pipeline.awc + goal.pipeline.appraisalBack}
                </span>{' '}
                active files in your pipeline
              </p>
            </div>
            <div className="flex gap-6 text-sm" data-id="element-238">
              <div className="text-center" data-id="element-239">
                <div className="text-2xl font-bold text-blue-600" data-id="element-240">
                  {goal.pipeline.awc}
                </div>
                <div className="text-slate-500" data-id="element-241">Need Action</div>
              </div>
              <div className="text-center" data-id="element-242">
                <div className="text-2xl font-bold text-amber-600" data-id="element-243">
                  {goal.pipeline.appraisalBack}
                </div>
                <div className="text-slate-500" data-id="element-244">Monitor</div>
              </div>
              <div className="text-center" data-id="element-245">
                <div className="text-2xl font-bold text-emerald-600" data-id="element-246">
                  {goal.ctcActual}
                </div>
                <div className="text-slate-500" data-id="element-247">Completed</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <FileListModal isOpen={isAwcModalOpen} onClose={() => setIsAwcModalOpen(false)} stage="awc" files={MOCK_AWC_FILES} userRole="processor" data-id="element-248" />
      <FileListModal isOpen={isAppraisalModalOpen} onClose={() => setIsAppraisalModalOpen(false)} stage="appraisalBack" files={MOCK_APPRAISAL_FILES} userRole="processor" data-id="element-249" />
    </>;
}